import {
  EndOrderDetails,
  firestoreDateParser,
  EndOrderDetailDigitalPayment,
} from '@pedix-workspace/utils';
import { convertProductData } from './productConverter';
import { withNestedConverter } from './utils';
import { parseMarketplaceOrderDates } from './utils/marketplace-order';
import { parseOrderTableDates } from './utils/order-table';

export const ORDER_SNAPSHOT_COLLECTION = 'ordersnapshot';

/**
 * Infers the payment provider from a payment link URL.
 * Used for backward compatibility when migrating from paymentLink to digitalPayment.
 */
function inferProviderFromUrl(url?: string): EndOrderDetailDigitalPayment['provider'] | undefined {
  if (!url) return undefined;

  if (url.includes('mercadopago')) return 'mercadopago';
  if (url.includes('zenrise')) return 'zenrise';
  if (url.includes('uala')) return 'uala';
  if (url.includes('chytapay')) return 'chytapay';
  if (url.includes('cucuru')) return 'cucuru';

  return undefined;
}

export const getOrderSnapshotConverter = withNestedConverter<EndOrderDetails>({
  fromFirestore(snapshot) {
    const orderSnapshot = <EndOrderDetails>snapshot.data();

    // Handle new digitalPayment field (priority)
    let digitalPayment = orderSnapshot.digitalPayment
      ? {
          ...orderSnapshot.digitalPayment,
          updated: firestoreDateParser(orderSnapshot.digitalPayment.updated),
        }
      : undefined;

    // On-read migration: convert legacy paymentLink to digitalPayment format
    if (!digitalPayment && orderSnapshot.paymentLink) {
      digitalPayment = {
        type: 'link',
        status: orderSnapshot.paymentLink.status,
        id: orderSnapshot.paymentLink.id,
        url: orderSnapshot.paymentLink.url,
        updated: firestoreDateParser(orderSnapshot.paymentLink.updated),
        provider: inferProviderFromUrl(orderSnapshot.paymentLink.url),
      };
    }

    return <EndOrderDetails>{
      ...orderSnapshot,
      id: snapshot.id,
      created: firestoreDateParser(orderSnapshot.created),
      updated: firestoreDateParser(orderSnapshot.updated),
      canceledDate: firestoreDateParser(orderSnapshot.canceledDate),
      inProgressDate: firestoreDateParser(orderSnapshot.inProgressDate),
      readyDate: firestoreDateParser(orderSnapshot.readyDate),
      shippedDate: firestoreDateParser(orderSnapshot.shippedDate),
      completedDate: firestoreDateParser(orderSnapshot.completedDate),
      cartItems: orderSnapshot.cartItems.map(cartItem => ({
        ...cartItem,
        product: convertProductData(cartItem.product.id, cartItem.product),
      })),
      tags: orderSnapshot.tags || [],
      internalNotes: orderSnapshot.internalNotes || '',
      status: orderSnapshot.status || 'completed',
      digitalPayment,
      marketplaceOrder: parseMarketplaceOrderDates(orderSnapshot.marketplaceOrder),
      table: parseOrderTableDates(orderSnapshot.table),
      payments: orderSnapshot.payments?.map(payment => ({
        ...payment,
        at: firestoreDateParser(payment.at),
      })),
      paymentLink: undefined, // Never return legacy field
      personalInformation: {
        ...orderSnapshot.personalInformation,
        phone: orderSnapshot.personalInformation.phone?.toString(),
      },
    };
  },
  toFirestore(orderSnapshot: EndOrderDetails): EndOrderDetails {
    return orderSnapshot;
  },
});
